/*
 * ohp-pdp.js — OptiHealth PDP & Shop interaction behaviors (Session 5)
 * ------------------------------------------------------------------
 * Vanilla JS, no framework. Drives the *presentation* layer only:
 *   1. Product gallery  — prev/next arrows + thumbnail strip.
 *   2. Qty/price selector — updates the Add-to-cart price label and the
 *      <form 'product'> hidden variant id (data-variant-id on each radio).
 *   3. Bundle configurator (Shop) — tier selector updates price / savings /
 *      per-month / value stack and the bundle form's hidden variant id.
 *   4. Duo gallery (Shop) — arrows + dot indicators carousel.
 *
 * The actual Ajax add-to-cart and cart-drawer open are owned by Dawn's
 * native <product-form> (product-form.js) — we only keep the hidden
 * variant id input in sync, so the cart line + thumbnail come straight
 * from the real Shopify variant.
 *
 * Re-init on Shopify theme-editor section load so previews stay live.
 */
(function () {
  'use strict';

  /* ---- 1. Product gallery ------------------------------------------- */
  function initGallery(root) {
    var galleries = root.querySelectorAll('[data-ohp-gallery]');
    Array.prototype.forEach.call(galleries, function (gallery) {
      if (gallery._ohpInit) return;
      gallery._ohpInit = true;

      var main = gallery.querySelector('[data-ohp-gallery-main]');
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-ohp-gallery-thumb]'));
      var prev = gallery.querySelector('[data-ohp-gallery-prev]');
      var next = gallery.querySelector('[data-ohp-gallery-next]');
      if (!main || !thumbs.length) return;

      var index = 0;
      function show(i) {
        index = (i + thumbs.length) % thumbs.length;
        var full = thumbs[index].getAttribute('data-full');
        if (full) main.src = full;
        thumbs.forEach(function (t, ti) {
          var active = ti === index;
          t.classList.toggle('is-active', active);
          if (active) {
            t.setAttribute('aria-current', 'true');
          } else {
            t.removeAttribute('aria-current');
          }
        });
      }

      thumbs.forEach(function (t, ti) {
        t.addEventListener('click', function () { show(ti); });
      });
      if (prev) prev.addEventListener('click', function () { show(index - 1); });
      if (next) next.addEventListener('click', function () { show(index + 1); });
    });
  }

  /* ---- 2. Qty / price selector -------------------------------------- */
  function initQtySelectors(root) {
    var selectors = root.querySelectorAll('[data-ohp-buy]');
    Array.prototype.forEach.call(selectors, function (buy) {
      if (buy._ohpInit) return;
      buy._ohpInit = true;

      var radios = Array.prototype.slice.call(buy.querySelectorAll('.ohp-qty__input'));
      var priceEl = buy.querySelector('[data-ohp-atc-price]');
      var hiddenId = buy.querySelector('input[name="id"]');
      if (!radios.length) return;

      function sync(radio) {
        if (!radio) return;
        if (priceEl) {
          var price = radio.getAttribute('data-price');
          if (price) priceEl.textContent = price;
        }
        if (hiddenId) {
          var vid = radio.getAttribute('data-variant-id');
          if (vid) {
            hiddenId.value = vid;
            hiddenId.removeAttribute('disabled');
          }
        }
      }

      radios.forEach(function (r) {
        r.addEventListener('change', function () { if (r.checked) sync(r); });
      });
      sync(radios.filter(function (r) { return r.checked; })[0] || radios[0]);
    });
  }

  /* ---- 3. Bundle configurator (Shop) -------------------------------- */
  function initBundle(root) {
    var bundles = root.querySelectorAll('[data-ohp-bundle]');
    Array.prototype.forEach.call(bundles, function (bundle) {
      if (bundle._ohpInit) return;
      bundle._ohpInit = true;

      var radios = Array.prototype.slice.call(bundle.querySelectorAll('.ohp-qty__input'));
      var priceEl = bundle.querySelector('[data-ohp-bundle-price]');     // ATC button price
      var wasEl = bundle.querySelector('[data-ohp-bundle-was]');         // value stack: bought separately
      var bundlePriceEl = bundle.querySelector('[data-ohp-bundle-total]'); // value stack: bundle price
      var saveEl = bundle.querySelector('[data-ohp-bundle-save]');       // value stack: you save
      var permonthEl = bundle.querySelector('[data-ohp-bundle-permonth]');
      var badgeEl = bundle.querySelector('[data-ohp-bundle-badge-pct]');
      var hiddenId = bundle.querySelector('input[name="id"]');
      if (!radios.length) return;

      function setText(el, val) { if (el && val != null && val !== '') el.textContent = val; }

      function sync(radio) {
        if (!radio) return;
        var price = radio.getAttribute('data-price');
        setText(priceEl, price);
        setText(bundlePriceEl, price);
        setText(wasEl, radio.getAttribute('data-was'));
        setText(saveEl, radio.getAttribute('data-save-amount'));
        setText(permonthEl, radio.getAttribute('data-permonth'));
        setText(badgeEl, radio.getAttribute('data-save-pct'));
        if (hiddenId) {
          var vid = radio.getAttribute('data-variant-id');
          if (vid) {
            hiddenId.value = vid;
            hiddenId.removeAttribute('disabled');
          }
        }
      }

      radios.forEach(function (r) {
        r.addEventListener('change', function () { if (r.checked) sync(r); });
      });
      sync(radios.filter(function (r) { return r.checked; })[0] || radios[0]);
    });
  }

  /* ---- 4. Duo gallery carousel (Shop, dot indicators) --------------- */
  function initCarousel(root) {
    var carousels = root.querySelectorAll('[data-ohp-carousel]');
    Array.prototype.forEach.call(carousels, function (carousel) {
      if (carousel._ohpInit) return;
      carousel._ohpInit = true;

      var main = carousel.querySelector('[data-ohp-carousel-main]');
      var dots = Array.prototype.slice.call(carousel.querySelectorAll('[data-ohp-carousel-dot]'));
      var prev = carousel.querySelector('[data-ohp-carousel-prev]');
      var next = carousel.querySelector('[data-ohp-carousel-next]');
      if (!main || !dots.length) return;

      var index = 0;
      function show(i) {
        index = (i + dots.length) % dots.length;
        var full = dots[index].getAttribute('data-full');
        if (full) main.src = full;
        dots.forEach(function (d, di) {
          var active = di === index;
          d.classList.toggle('is-active', active);
          d.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }

      dots.forEach(function (d, di) {
        d.addEventListener('click', function () { show(di); });
      });
      if (prev) prev.addEventListener('click', function () { show(index - 1); });
      if (next) next.addEventListener('click', function () { show(index + 1); });
    });
  }

  function initAll(root) {
    var scope = root || document;
    initGallery(scope);
    initQtySelectors(scope);
    initBundle(scope);
    initCarousel(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme editor: re-init the freshly loaded section.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
